import type { EnrichedMatch, MatchRow, PlayerStanding } from './types';
import { getToken } from './auth';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

function authHeaders(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'omit',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { const j = await res.json() as any; if (j?.error) msg = j.error; } catch {}
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export interface LeaderboardResponse {
  standings: PlayerStanding[];
  lastPoll: string | null;
}
export const getLeaderboard = () => json<LeaderboardResponse>('/api/leaderboard');

export interface LeaderboardHistory {
  dates: string[];
  series: { player: string; actual: number[]; projected: number[] }[];
}
export const getLeaderboardHistory = () => json<LeaderboardHistory>('/api/leaderboard/history');

export interface PlayerResponse {
  player: { name: string; teams: { code: string; name: string; flag: string }[] };
  standing?: PlayerStanding;
  matches: (MatchRow & {
    ownedSide: 'home' | 'away' | 'both' | null;
    home_owner: string | null;
    away_owner: string | null;
    home_flag: string | null;
    away_flag: string | null;
  })[];
}
export const getPlayer = (name: string) =>
  json<PlayerResponse>(`/api/players/${encodeURIComponent(name)}`);

export interface ScheduleResponse {
  matches: EnrichedMatch[];
}
export const getSchedule = () => json<ScheduleResponse>('/api/schedule');

export interface PlayersResponse {
  players: { name: string; teamCount: number }[];
}
export const getPlayers = () => json<PlayersResponse>('/api/players');

export interface GroupsResponse {
  groups: {
    label: string;
    earliestKickoff: string;
    teams: {
      code: string; name: string; flag: string; owner: string | null;
      played: number; wins: number; draws: number; losses: number;
      goalsFor: number; goalsAgainst: number; goalDifference: number; points: number;
    }[];
  }[];
  lastPoll: string | null;
}
export const getGroups = () => json<GroupsResponse>('/api/groups');

export interface KnockoutResponse {
  basis: 'pre-tournament' | 'in-progress' | 'post-group';
  teams: {
    code: string; name: string; flag: string; owner: string | null;
    actualPoints: number; expectedRemainingGroup: number;
    strength: number; cupChance: number; finalChance: number; semiChance: number;
  }[];
  lastPoll: string | null;
}
export const getKnockout = () => json<KnockoutResponse>('/api/knockout');

export const triggerPoll = async (): Promise<void> => {
  await fetch(`${API_BASE}/api/poll`, { method: 'POST' });
};

// ---------- Auth ----------

export interface LoginResponse { token: string; name: string }

export const login = (name: string, password: string) =>
  json<LoginResponse>('/api/auth/login', { method: 'POST', body: JSON.stringify({ name, password }) });

// ---------- Bets ----------

export type BetSide = 'yes' | 'no';
export type BetStatus = 'pending' | 'open' | 'declined' | 'cancelled' | 'active' | 'settled' | 'voided';
export type BetWinner = 'proposer' | 'opponent' | 'void' | null;

export interface BetRow {
  id: string;
  proposer: string;
  opponent: string;
  match_id: string | null;
  description: string;
  stake: number;
  currency: string;
  status: BetStatus;
  winner: BetWinner;
  message: string | null;
  created_at: string;
  responded_at: string | null;
  settled_at: string | null;
  resolution_proposer_pick: BetWinner;
  resolution_opponent_pick: BetWinner;
  proposer_side: BetSide | null;
  proposer_stake: number | null;
  opponent_stake: number | null;
  is_marketplace: number;
  expires_at: string | null;
}

/** Effective per-side stake; falls back to legacy `stake` for old 1:1 bets. */
export function effectiveStakes(b: Pick<BetRow, 'stake' | 'proposer_stake' | 'opponent_stake'>): { proposer: number; opponent: number } {
  return {
    proposer: b.proposer_stake ?? b.stake,
    opponent: b.opponent_stake ?? b.stake,
  };
}

export interface LedgerEntry {
  opponent: string;
  net: number;
  betsCount: number;
  wins: number;
  losses: number;
}

export interface BetsResponse {
  user: string;
  bets: BetRow[];
  ledger: LedgerEntry[];
}

export const getBets = () => json<BetsResponse>('/api/bets');

export interface NewBet {
  opponent?: string;
  match_id?: string | null;
  description: string;
  proposer_side: BetSide;
  proposer_stake: number;
  opponent_stake: number;
  message?: string;
  is_marketplace?: boolean;
  expires_at?: string | null;
}

export const adminResolveMatch = (
  id: string,
  key: string,
  body: { winner: 'home' | 'away'; ended_phase: 'reg' | 'ot' | 'pk'; home_pk_score?: number | null; away_pk_score?: number | null },
) =>
  fetch(`${API_BASE}/api/admin/matches/${id}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Key': key },
    body: JSON.stringify(body),
  }).then(async (r) => {
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? 'Failed');
    return r.json();
  });

export const adminImpersonate = (user: string, key: string) =>
  fetch(`${API_BASE}/api/admin/impersonate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Key': key },
    body: JSON.stringify({ user }),
  }).then(async (r) => {
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? 'Failed');
    return r.json() as Promise<{ token: string; name: string }>;
  });

export const createBet = (body: NewBet) =>
  json<{ bet: BetRow }>('/api/bets', { method: 'POST', body: JSON.stringify(body) });

export const respondToBet = (id: string, action: 'accept' | 'decline') =>
  json<{ bet: BetRow }>(`/api/bets/${id}/respond`, { method: 'POST', body: JSON.stringify({ action }) });

export const cancelBet = (id: string) =>
  json<{ bet: BetRow }>(`/api/bets/${id}/cancel`, { method: 'POST' });

export const resolveBet = (id: string, winner: 'proposer' | 'opponent' | 'void') =>
  json<{ bet: BetRow }>(`/api/bets/${id}/resolve`, { method: 'POST', body: JSON.stringify({ winner }) });

export const claimBet = (id: string) =>
  json<{ bet: BetRow }>(`/api/bets/${id}/claim`, { method: 'POST' });

export const getPublicBets = () => json<{ bets: BetRow[] }>('/api/bets/public');
export const getBetById = (id: string) => json<{ bet: BetRow }>(`/api/bets/${id}`);

// ---------- Notifications ----------

export type NotifKind =
  | 'bet_invited' | 'bet_accepted' | 'bet_declined' | 'bet_cancelled' | 'bet_resolved'
  | 'news' | 'match_kickoff' | 'match_ended' | 'team_advanced';

export interface NotifRow {
  id: string;
  user: string;
  kind: NotifKind;
  title: string;
  body: string | null;
  link: string | null;
  payload: string | null;
  created_at: string;
  read_at: string | null;
}

export const getNotifications = () => json<{ items: NotifRow[]; unread: number }>('/api/notifications');
export const getUnreadCount = () => json<{ unread: number }>('/api/notifications/unread-count');
export const markNotifRead = (id: string) => json<{ ok: true }>(`/api/notifications/${id}/read`, { method: 'POST' });
export const markAllNotifRead = () => json<{ ok: true }>('/api/notifications/read-all', { method: 'POST' });

export type NotifPrefCategory = 'bet' | 'match' | 'goal' | 'team' | 'news' | 'digest' | 'announcement';
export type NotifPrefs = Record<NotifPrefCategory, boolean>;
export const getMyNotifPrefs = () => json<{ prefs: NotifPrefs; defaults: NotifPrefs }>('/api/users/me/prefs');
export const setMyNotifPrefs = (patch: Partial<NotifPrefs>) =>
  json<{ ok: true; prefs: NotifPrefs }>('/api/users/me/prefs', { method: 'POST', body: JSON.stringify(patch) });

// ---------- News ----------

export interface NewsItem {
  id: string;
  source: string;
  title: string;
  description: string | null;
  link: string;
  image_url: string | null;
  published_at: string;
  fetched_at: string;
  team_codes: string | null;
}

export const getNews = (team?: string) => json<{ items: NewsItem[] }>(`/api/news${team ? `?team=${encodeURIComponent(team)}` : ''}`);

// ---------- Hot Players ----------

export interface HotPlayer {
  id: string; name: string; team: string; teamCode: string;
  goals: number; assists: number; appearances: number; score: number;
}

export const getHotPlayers = () => json<{ players: HotPlayer[]; note?: string }>('/api/players-hot');

// ---------- Match detail ----------

export type MatchEventType =
  | 'goal' | 'own-goal' | 'penalty-goal' | 'penalty-miss'
  | 'yellow-card' | 'red-card' | 'yellow-red-card'
  | 'substitution' | 'var' | 'kickoff' | 'half-end' | 'full-time' | 'other';

export interface MatchEvent {
  id: string;
  type: MatchEventType;
  typeText: string;
  text: string;
  shortText: string | null;
  minute: string;
  period: number | null;
  teamId: string | null;
  teamSide: 'home' | 'away' | null;
  participants: { id: string | null; name: string }[];
  scoringPlay: boolean;
}

export interface PlayerLine {
  id: string | null;
  name: string;
  shortName: string;
  jersey: string | null;
  position: string | null;
  starter: boolean;
  subbedIn: boolean;
  subbedOut: boolean;
  formationPlace: string | null;
  stats: {
    goals: number;
    assists: number;
    yellowCards: number;
    redCards: number;
    shotsOnTarget: number;
    saves: number;
    foulsCommitted: number;
  };
}

export interface RosterSide {
  teamId: string | null;
  teamCode: string;
  teamName: string;
  side: 'home' | 'away' | null;
  formation: string | null;
  starters: PlayerLine[];
  bench: PlayerLine[];
}

export interface MatchSummary {
  venue: { name: string | null; city: string | null };
  attendance: number | null;
  broadcasts: { name: string; market: string | null }[];
  headToHead: { homeWins: number; awayWins: number; draws: number; games: number };
  rosters: RosterSide[];
  events: MatchEvent[];
  commentary: { minute: string; text: string }[];
  status: { detail: string | null; period: number | null; clock: string | null };
}

export interface AIPickRow {
  match_id: string;
  pick: '1' | 'X' | '2';
  home_score: number;
  away_score: number;
  reasoning: string | null;
  generated_at: string;
  model: string;
}

export interface MatchDetail {
  match: import('./types').EnrichedMatch;
  summary: MatchSummary | null;
  preview: string | null;
  recap: string | null;
  aiPick: AIPickRow | null;
  insights: string[] | null;
}

export const getMatch = (id: string) => json<MatchDetail>(`/api/matches/${id}`);
export const requestPreview = (id: string) => json<{ preview: string | null }>(`/api/matches/${id}/preview`, { method: 'POST' });
export const requestInsights = (id: string) => json<{ insights: string[] | null }>(`/api/matches/${id}/insights`, { method: 'POST' });
export const askMatchAI = (id: string, question: string) => json<{ answer: string | null }>(`/api/matches/${id}/chat`, { method: 'POST', body: JSON.stringify({ question }) });
export const aiBetTrashTalk = (body: { description: string; stake: number; opponent: string }) =>
  json<{ text: string | null }>('/api/bets/trash-talk', { method: 'POST', body: JSON.stringify(body) });

export interface CaptainAdviceTeam { code: string; name: string; flag: string; score: number }
export interface CaptainAdvice { round: Round; ranked: CaptainAdviceTeam[]; best: CaptainAdviceTeam | null }
export const getCaptainAdvice = (round: Round) => json<CaptainAdvice>(`/api/captain/advice/${round}`);

// ---------- Predictions ----------

export type PredPick = '1' | 'X' | '2';
export interface PredictionRow {
  match_id: string;
  user: string;
  pick: PredPick;
  home_score: number | null;
  away_score: number | null;
  points: number | null;
  created_at: string;
  updated_at: string;
  scored_at: string | null;
}
export const getPredictionsLeaderboard = () => json<{ leaderboard: { user: string; total: number; correct: number; exact: number; predictions: number }[] }>('/api/predictions');
export const getMyPredictions = () => json<{ items: PredictionRow[] }>('/api/predictions/me');
export const getPredictionsForMatch = (matchId: string) => json<{ items: PredictionRow[] }>(`/api/predictions?match=${matchId}`);
export const submitPrediction = (body: { match_id: string; pick: PredPick; home_score?: number | null; away_score?: number | null }) =>
  json<{ prediction: PredictionRow }>('/api/predictions', { method: 'POST', body: JSON.stringify(body) });

// ---------- Achievements + rivalries ----------

export interface Achievement { id: string; title: string; body: string; earned: boolean; earnedAt: string | null }
export interface RivalryEntry {
  opponent: string;
  bets: { wins: number; losses: number; net: number; total: number };
  fantasy: { mineWins: number; theirWins: number; mineProj: number; theirProj: number };
}
export const getAchievements = (user: string) => json<{ achievements: Achievement[]; rivalries: RivalryEntry[] }>(`/api/achievements/${encodeURIComponent(user)}`);

// ---------- Comments + reactions ----------

export interface CommentRow { id: string; match_id: string; user: string; body: string; created_at: string }
export const getMatchComments = (id: string) => json<{ comments: CommentRow[]; reactions: { emoji: string; users: string[] }[] }>(`/api/matches/${id}/comments`);
export const postMatchComment = (id: string, body: string) => json<{ comment: CommentRow }>(`/api/matches/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) });
export const deleteMatchComment = (matchId: string, commentId: string) => json<{ ok: boolean }>(`/api/matches/${matchId}/comments/${commentId}/delete`, { method: 'POST' });
export const toggleMatchReaction = (id: string, emoji: string) => json<{ added: boolean }>(`/api/matches/${id}/reactions`, { method: 'POST', body: JSON.stringify({ emoji }) });

// ---------- Captain ----------

export type Round = 'group_md1' | 'group_md2' | 'group_md3' | 'r32' | 'r16' | 'qf' | 'sf' | 'final';
export interface CaptainPickRow { round: Round; user: string; team_code: string; created_at: string; updated_at: string }
export const getCaptainRounds = () => json<{ rounds: { round: Round; lockAt: string }[] }>('/api/captain/rounds');
export const getMyCaptains = () => json<{ picks: CaptainPickRow[] }>('/api/captain/me');
export const getAllCaptains = () => json<{ picks: CaptainPickRow[] }>('/api/captain/all');
export const setCaptain = (round: Round, team_code: string) => json<{ pick: CaptainPickRow }>('/api/captain', { method: 'POST', body: JSON.stringify({ round, team_code }) });

export interface CaptainLBEntry { user: string; earnedBonus: number; expectedBonus: number }
export const getCaptainLeaderboard = () => json<{ leaderboard: CaptainLBEntry[] }>('/api/captain/leaderboard');

// ---------- Team detail ----------

export interface TeamDetail {
  team: { code: string; name: string; flag: string; aliases: string[]; owner: string | null };
  matches: (import('./types').EnrichedMatch & { side: 'home' | 'away' })[];
  group: {
    label: string;
    teams: { code: string; name: string; flag: string; owner: string | null; played: number; wins: number; draws: number; losses: number; goalsFor: number; goalsAgainst: number; goalDifference: number; points: number }[];
    row: { code: string; played: number; wins: number; draws: number; losses: number; goalDifference: number; points: number } | null;
  } | null;
  knockout: { code: string; cupChance: number; semiChance: number; finalChance: number; strength: number } | null;
  news: { id: string; source: string; title: string; description: string | null; link: string; image_url: string | null; published_at: string }[];
  captains: { round: string; user: string; updated_at: string }[];
}
export const getTeam = (code: string) => json<TeamDetail>(`/api/teams/${encodeURIComponent(code)}`);

// ---------- Props ----------

export interface MatchProps {
  home: number; draw: number; away: number;
  expectedGoals: { home: number; away: number; total: number };
  totals: { line: number; over: number; under: number }[];
  btts: { yes: number; no: number };
  cleanSheet: { home: number; away: number };
  doubleChance: { '1X': number; '12': number; 'X2': number };
  htft: { homeHome: number; drawHome: number; awayAway: number };
  winToNil: { home: number; away: number };
  topCorrectScores: { home: number; away: number; prob: number }[];
}
export const getMatchProps = (id: string) => json<{ props: MatchProps | null }>(`/api/matches/${id}/props`);

// ---------- Live win-prob snapshots ----------

export interface WinProbSnapshot {
  match_id: string;
  minute: number;
  p_home: number;
  p_draw: number;
  p_away: number;
  home_score: number;
  away_score: number;
  captured_at: string;
}
export const getWinProb = (id: string) =>
  json<{ snapshots: WinProbSnapshot[] }>(`/api/matches/${id}/winprob`);

// ---------- Streaks + today leaderboard ----------

export interface UserStreak { user: string; current: number; best: number; scored: number }
export const getStreaks = () => json<{ streaks: UserStreak[] }>('/api/streaks');

export interface DayLeaderEntry {
  user: string;
  prediction_points: number;
  bet_winnings: number;
  total: number;
  matches: number;
}
export const getTodayLeaderboard = () =>
  json<{ date: string; entries: DayLeaderEntry[]; matches: number }>('/api/leaderboard/today');

// ---------- Tournament state + countdown ----------

export interface TournamentState {
  tournamentStarted: boolean;
  firstKickoffAt: string | null;
  nextMatch: {
    id: string; home_name: string; away_name: string;
    home_flag: string | null; away_flag: string | null;
    utc_kickoff: string; stage: string;
  } | null;
  liveMatch: {
    id: string; home_name: string; away_name: string;
    home_flag: string | null; away_flag: string | null;
  } | null;
}
export const getTournamentState = () => json<TournamentState>('/api/tournament-state');

// ---------- Multi-model tournament forecast ----------

export interface TeamForecast {
  code: string; name: string; flag: string;
  cupChance: number; runnerUpChance: number;
}
export interface ModelForecast {
  id: 'mc' | 'strength' | 'ai';
  label: string;
  teams: TeamForecast[];
  note: string | null;
  generatedAt: string | null;
}
export interface UserExpectedScore {
  user: string;
  champion: string | null;
  runner_up: string | null;
  perModel: { id: string; expected: number; cupChance: number; runnerUpChance: number }[];
  aggregate: number;
}
export const getTournamentForecast = () =>
  json<{ models: ModelForecast[]; scores: UserExpectedScore[] }>('/api/tournament-forecast');

export interface TeamTournamentProps {
  code: string; name: string; flag: string; groupLabel: string | null;
  strength: number;
  cupChance: number; finalChance: number; semiChance: number;
  groupWinChance: number;
  advanceR32: number; reachR16: number; reachQF: number;
}
export const getTeamProps = (code: string) => json<{ props: TeamTournamentProps }>(`/api/teams/${encodeURIComponent(code)}/props`);
export const getAllTournamentProps = () => json<{ teams: TeamTournamentProps[] }>('/api/tournament-props');

// ---------- Tournament-long predictions ----------

export interface TournamentPred { user: string; champion: string | null; runner_up: string | null; top_scorer: string | null; dark_horse: string | null; created_at: string; updated_at: string; locked_at: string | null }
export const getTournamentPreds = () => json<{ items: TournamentPred[]; locked: boolean }>('/api/tournament-preds');
export const setTournamentPred = (body: Partial<TournamentPred>) => json<{ prediction: TournamentPred }>('/api/tournament-preds', { method: 'POST', body: JSON.stringify(body) });

// ---------- Activity ----------

export interface ActivityItem { id: string; kind: string; title: string; body: string | null; link: string | null; created_at: string }
export const getActivity = () => json<{ items: ActivityItem[] }>('/api/activity');

// ---------- Power-rankings drift ----------

export interface KnockoutDrift {
  dates: string[];
  series: { team_code: string; name: string; flag: string; owner: string | null; series: { day: string; cup_chance: number; strength: number }[] }[];
}
export const getKnockoutDrift = () => json<KnockoutDrift>('/api/knockout/drift');

// ---------- Recap ----------

export const requestRecap = (id: string) => json<{ recap: string | null }>(`/api/matches/${id}/recap`, { method: 'POST' });
